/**
 * "Sign in with Microsoft" — Microsoft ships no hosted button, so this follows
 * its published branding (learn.microsoft.com, "Sign in with Microsoft
 * branding guidelines"): the unaltered four-square logo, 12px between logo and
 * the words "Sign in with Microsoft", and a light (white fill, grey border) or
 * dark (dark fill, white text) scheme — here the elevated-surface/border/text tokens,
 * which are exactly that pair in our light and dark themes. Same width and
 * height as Google's button beside it (GIS "large": 40px, at most 400px).
 *
 * MSAL loads only when this is pressed (`signInWithMicrosoftPopup`). A closed
 * or blocked popup resets quietly; anything else goes to `onError`.
 *
 * Plain markup + tokens only — no design-system import, as with Google's.
 */
import { useRef, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { isMicrosoftPopupCancel, signInWithMicrosoftPopup } from "../actions/microsoftPopup.js";

export interface MicrosoftSignInButtonProps {
  /** Entra application (client) id. */
  clientId: string;
  /** Receives MSAL's `result.idToken` — never the access token. */
  onIdToken: (idToken: string) => void;
  /** Microsoft answered with an error (not a cancel) or MSAL failed to load. */
  onError: () => void;
  disabled?: boolean;
}

export function MicrosoftSignInButton({
  clientId,
  onIdToken,
  onError,
  disabled = false,
}: MicrosoftSignInButtonProps): JSX.Element {
  const { t } = useTranslation("auth");
  const [pending, setPending] = useState(false);
  // Each press supersedes the last; a late answer from an older one is dropped.
  const press = useRef(0);

  const onClick = (): void => {
    const id = ++press.current;
    const current = (): boolean => press.current === id;
    setPending(true);
    signInWithMicrosoftPopup(clientId, () => {
      if (current()) setPending(false);
    })
      .then((idToken) => {
        if (!current()) return;
        setPending(false);
        onIdToken(idToken);
      })
      .catch((err: unknown) => {
        if (!current()) return;
        setPending(false);
        if (!isMicrosoftPopupCancel(err)) onError();
      });
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || pending}
      aria-busy={pending}
      className="mx-auto flex h-10 w-full max-w-[400px] items-center justify-center gap-[12px] rounded-xs border border-border-strong bg-surface-elevated px-[12px] text-sm font-semibold text-default hover:bg-surface-muted focus-visible:focus-ring disabled:cursor-not-allowed disabled:opacity-60"
    >
      <MicrosoftLogo />
      <span>{t("login.microsoft_sign_in")}</span>
    </button>
  );
}

/* eslint-disable @linkhr/linkhr/no-hex-colors -- Microsoft's logo: brand-locked square colours, never themed or altered (Microsoft branding guidelines) */
function MicrosoftLogo(): JSX.Element {
  return (
    <svg width="21" height="21" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
/* eslint-enable @linkhr/linkhr/no-hex-colors */
